/**
 * EntitySystemPicker - Catalog 页面按 System 筛选组件
 *
 * 遵循 Backstage EntityFilter 模式：
 * 1. EntitySystemFilter 实现 EntityFilter 接口（服务端 + 客户端过滤）
 * 2. EntitySystemPicker 从 Catalog API 获取真实的 System 实体作为选项
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
} from '@material-ui/core';
import {
  useEntityList,
  EntityFilter,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';

// ── Filter 类 ───────────────────────────────────────────────────────────────

/** 按 spec.system 过滤实体 */
export class EntitySystemFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return { 'spec.system': this.values };
  }

  filterEntity(entity: Entity): boolean {
    const system = (entity.spec as any)?.system;
    if (!system) return this.values.length === 0;
    return this.values.some(
      v => v.toLowerCase() === String(system).toLowerCase(),
    );
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

// ── Picker 组件 ─────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
  select: {
    fontSize: '0.875rem',
  },
}));

export const EntitySystemPicker = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { updateFilters, queryParameters } = useEntityList();

  const [selectedSystem, setSelectedSystem] = useState<string>('');
  const [availableSystems, setAvailableSystems] = useState<string[]>([]);

  // 从 Catalog API 获取真实的 System 实体
  useEffect(() => {
    const fetchSystems = async () => {
      try {
        const { items } = await catalogApi.getEntities({
          filter: { kind: 'System' },
          fields: ['metadata.name'],
        });
        const names = items
          .map(e => e.metadata.name)
          .sort((a, b) => a.localeCompare(b));
        setAvailableSystems(names);
      } catch (err) {
        console.error('[EntitySystemPicker] Failed to fetch systems:', err);
      }
    };
    fetchSystems();
  }, [catalogApi]);

  // 从 URL query params 恢复状态
  useEffect(() => {
    const queryValue = (queryParameters as any).system;
    if (queryValue) {
      const value = Array.isArray(queryValue) ? queryValue[0] : queryValue;
      setSelectedSystem(value || '');
    }
  }, [queryParameters]);

  const handleChange = (value: string) => {
    setSelectedSystem(value);
    updateFilters({
      system: value ? new EntitySystemFilter([value]) : undefined,
    } as any);
  };

  // 如果没有 system 可选，不渲染
  if (availableSystems.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.label}>System</Typography>
      <FormControl fullWidth size="small">
        <Select
          value={selectedSystem}
          onChange={e => handleChange(e.target.value as string)}
          displayEmpty
          className={classes.select}
          variant="outlined"
        >
          <MenuItem value="">all</MenuItem>
          {availableSystems.map(system => (
            <MenuItem key={system} value={system}>
              {system}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
